import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function DocumentUpload() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [cards, setCards] = useState([]);
  const [cardholderId, setCardholderId] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/cardholder/cards");
        const active = (data.cards || []).filter((c) => !c.lost_reported && c.is_active !== false);
        setCards(active);
        if (active.length === 1) setCardholderId(active[0].id);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [user]);

  const submit = async (event) => {
    event.preventDefault();
    if (!file || !cardholderId || !title.trim()) return;
    setBusy(true);
    setError("");
    const body = new FormData();
    body.append("file", file);
    body.append("cardholder_id", cardholderId);
    body.append("title", title.trim());
    try {
      const { data } = await api.post("/documents", body);
      setResult(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    const { data } = await api.get(`/documents/${result.id}/stamped.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${result.title || "document"}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const print = async () => {
    const { data } = await api.get(`/documents/${result.id}/stamped.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    window.open(url, "_blank", "noopener");
  };

  if (loading) {
    return (
      <div>
        <Nav />
        <div className="px-4 py-20 text-center label-tag">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div>
        <Nav />
        <div className="max-w-md mx-auto px-4 py-12">
          <Link to="/cardholder/login?next=/documents/new" className="label-tag underline">
            Log in to upload a document
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Nav />
      <div className="max-w-md mx-auto px-4 py-8">
        <div className="label-tag text-neutral-500">Document seal</div>
        <h1 className="font-display font-black text-3xl mt-1">Upload a qualification</h1>
        <p className="text-sm text-neutral-700 mt-2">
          We stamp a Twin-Matrix on page 1, read the document, and store the holder name.
          Print the PDF at 100% — do not scale to fit.
        </p>
        {error && <p className="mt-3 label-tag text-red-600">{error}</p>}

        {result ? (
          <div className="mt-6 border-2 border-black bg-[color:var(--cc-yellow)] p-4" data-testid="document-uploaded">
            <p className="label-tag">Stamped</p>
            <h2 className="font-display font-black text-2xl mt-1">{result.title}</h2>
            <p className="text-sm mt-2">
              Holder: <strong>{result.holder_first_name || "—"} {result.holder_surname || ""}</strong>
            </p>
            <p className="label-tag text-xs mt-2">Seal {result.asset_code}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={download} className="label-tag px-3 py-2 bg-black text-white brutal-btn">
                Download
              </button>
              <button type="button" onClick={print} className="label-tag px-3 py-2 border-2 border-black bg-white brutal-btn">
                Open to print
              </button>
              <button type="button" onClick={() => nav("/documents")} className="label-tag px-3 py-2 underline">
                My documents
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 border-2 border-black bg-white p-4 space-y-3">
            <label className="block">
              <span className="label-tag">Card</span>
              <select
                className="mt-1 w-full border border-black px-3 py-2"
                value={cardholderId}
                onChange={(e) => setCardholderId(e.target.value)}
                required
              >
                <option value="">Select a card</option>
                {cards.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} {c.full_name ? `· ${c.full_name}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="label-tag">Title</span>
              <input
                className="mt-1 w-full border border-black px-3 py-2"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Trade test certificate"
                required
              />
            </label>
            <label className="block">
              <span className="label-tag">PDF</span>
              <input
                className="mt-1 block w-full text-sm"
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                required
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="w-full label-tag px-4 py-3 bg-black text-white brutal-btn disabled:opacity-60"
            >
              {busy ? "Reading and stamping…" : "Upload"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
